import { useMemo } from "react";

import {
  useCountDocumentJobs,
  useListCareProtocolOpportunities,
} from "@elation/api-client/src/stitch-web";

import {
  isStaleCursorError,
  shouldRetryCursorQueryError,
  useCursorPagination,
  useResetStaleCursor,
} from "@src/shared/hooks/useCursorPagination";

import { CareProtocolOpportunityListSkeleton } from "./CareProtocolOpportunityListSkeleton";
import { CareProtocolOpportunityUploadGroup } from "./CareProtocolOpportunityUploadGroup";
import { EmptyCareProtocolOpportunities } from "./EmptyCareProtocolOpportunities";
import { ErrorCareProtocolOpportunities } from "./ErrorCareProtocolOpportunities";
import {
  UploadHistoryProcessingRow,
  UploadHistoryTable,
} from "./UploadHistoryTable";
import { groupCareProtocolOpportunitiesByDate } from "./uploadHistoryGrouping";
import {
  UPLOAD_HISTORY_WINDOW,
  type UploadHistoryWindow,
} from "./uploadHistoryWindow";

export type ExtractedCareGapsUploadState = ReturnType<
  typeof useExtractedCareGapsUploadState
>;

export function useExtractedCareGapsUploadState({
  shouldPoll,
  window = UPLOAD_HISTORY_WINDOW,
}: {
  shouldPoll: boolean;
  window?: UploadHistoryWindow;
}) {
  const {
    currentCursor,
    goToNextPage,
    goToPreviousPage,
    hasPrevPage,
    resetCursor,
  } = useCursorPagination();
  const query = useListCareProtocolOpportunities(
    {
      cursor: currentCursor,
      ...window,
    },
    {
      query: {
        refetchInterval: shouldPoll && !currentCursor ? 5_000 : false,
        retry: shouldRetryCursorQueryError,
      },
    },
  );
  const documentJobsCountQuery = useCountDocumentJobs({
    query: {
      enabled: !currentCursor,
      refetchInterval: shouldPoll && !currentCursor ? 5_000 : false,
    },
  });
  const items = query.data?.data.items ?? [];
  const uploadGroups = useMemo(
    () => groupCareProtocolOpportunitiesByDate(items),
    [items],
  );
  const activeUploadCount = documentJobsCountQuery.data?.data.count ?? 0;
  const nextCursor = query.data?.data.nextCursor ?? null;
  const isStaleCursor = isStaleCursorError({
    currentCursor,
    error: query.error,
    isError: query.isError,
  });
  const hasNextPage = Boolean(nextCursor);

  useResetStaleCursor(isStaleCursor, resetCursor);

  return {
    activeUploadCount,
    currentCursor,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPrevPage,
    isError: query.isError && !isStaleCursor,
    isLoading: query.isLoading || isStaleCursor,
    nextCursor,
    resetCursor,
    uploadGroups,
  };
}

export function ExtractedCareGapsContent({
  isVisible,
  searchTerm = "",
  shouldPollForDocuments,
  state,
}: {
  isVisible: boolean;
  searchTerm?: string;
  shouldPollForDocuments: boolean;
  state: ExtractedCareGapsUploadState;
}) {
  if (state.isLoading) {
    return <CareProtocolOpportunityListSkeleton />;
  }

  if (state.isError) {
    return <ErrorCareProtocolOpportunities />;
  }

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const uploadGroups = state.uploadGroups.filter((group) => {
    if (!normalizedSearchTerm) return true;
    return group.items.some((item) =>
      [
        item.description,
        item.patient.firstName,
        item.patient.lastName,
        item.patient.dob,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearchTerm),
    );
  });

  if (uploadGroups.length === 0 && state.activeUploadCount === 0) {
    return (
      <>
        <EmptyCareProtocolOpportunities />
      </>
    );
  }

  return (
    <>
      <UploadHistoryTable label="Care protocol opportunities">
        <UploadHistoryProcessingRow count={state.activeUploadCount} />
        {uploadGroups.map((group, index) => (
          <CareProtocolOpportunityUploadGroup
            key={group.date}
            defaultOpen={index === 0}
            date={group.date}
            isVisible={isVisible}
            items={group.items}
            searchTerm={searchTerm}
            shouldPollForDocuments={shouldPollForDocuments}
            uploadedAt={group.uploadedAt}
          />
        ))}
      </UploadHistoryTable>
    </>
  );
}

export function ExtractedCareGapsTab() {
  const state = useExtractedCareGapsUploadState({ shouldPoll: true });

  return (
    <ExtractedCareGapsContent
      isVisible
      shouldPollForDocuments={state.activeUploadCount > 0}
      state={state}
    />
  );
}
